// ReportRecipientsPanel — قائمة بيضاء صريحة لمستلمي كل تقرير من الخمسة.
// اختيار من حسابات الفريق فقط (username) — لا حقل رقم حرّ إطلاقًا.
import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function ReportRecipientsPanel() {
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState({}); // template_name -> selected username

  const load = useCallback(async () => {
    try {
      const r = await api.get("/reports/recipients");
      setReports(r.data?.reports || []);
    } catch (e) {
      toast.error("تعذّر تحميل قوائم المستلمين");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addOne = async (tpl) => {
    const username = pick[tpl];
    if (!username) { toast.error("اختر حسابًا من القائمة أولًا"); return; }
    setBusy(true);
    try {
      await api.post(`/reports/recipients/${tpl}/add`, { username });
      toast.success("أُضيف المستلم");
      setPick((p) => ({ ...p, [tpl]: "" }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّرت الإضافة");
    } finally { setBusy(false); }
  };

  const removeOne = async (tpl, username) => {
    setBusy(true);
    try {
      await api.post(`/reports/recipients/${tpl}/remove`, { username });
      toast.success("أُزيل المستلم");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّرت الإزالة");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="report-recipients-panel"
         style={{ direction: "rtl", background: "#fff", borderRadius: 14,
                   border: "1px solid #F5F5F4", padding: 16, marginBottom: 24 }}>
      <h3 style={{ margin: "0 0 4px", color: "#7C2D12", fontSize: 15 }}>
        👥 مستلمو كل تقرير (قائمة بيضاء صريحة)
      </h3>
      <div style={{ color: "#7C2D12", background: "#FEF3C7", borderRadius: 8,
                     padding: "6px 10px", fontSize: 12, marginBottom: 12, lineHeight: 1.6 }}
           data-testid="report-recipients-notice">
        🛡 الاختيار مقصور على حسابات الفريق الإداري فقط — <b>لا يوجد حقل رقم حرّ</b>،
        حمايةً من وصول أي رسالة لعميل. مستلم فعلي = يحمل الدور المناسب AND مُدرَج هنا صراحةً.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
        {reports.map((rep) => {
          const alreadyIn = new Set(rep.entries || []);
          const options = (rep.candidates || []).filter((c) => !alreadyIn.has(c.username));
          return (
            <div key={rep.template_name}
                 data-testid={`recipients-card-${rep.template_name}`}
                 style={{ border: "1px solid #F5F5F4", borderRadius: 12, padding: 12 }}>
              <div style={{ fontWeight: 700, color: "#292524", fontSize: 13 }}>{rep.label}</div>
              <code style={{ fontSize: 11, color: "#78716C" }}>{rep.template_name}</code>
              <div style={{ fontSize: 11, color: "#78716C", marginTop: 4 }}>
                الأدوار المسموحة: {(rep.allowed_roles || []).join("، ") || "—"} <span style={{ opacity: 0.7 }}>(+ المالك admin)</span>
              </div>

              {/* المستلمون الفعليون الحاليون */}
              <div style={{ marginTop: 10, fontSize: 12, color: "#57534E" }}>
                📬 مستلمون فعليون ({(rep.resolved || []).length}):
              </div>
              <div data-testid={`recipients-resolved-${rep.template_name}`}
                   style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6 }}>
                {(rep.resolved || []).length === 0 ? (
                  <span style={{ fontSize: 12, color: "#B45309" }}>لا أحد بعد</span>
                ) : (rep.resolved || []).map((p) => (
                  <span key={p.username}
                        data-testid={`recipient-chip-${rep.template_name}-${p.username}`}
                        style={{ background: "#DCFCE7", color: "#166534", borderRadius: 999,
                                  padding: "3px 8px", fontSize: 12, display: "inline-flex", gap: 6, alignItems: "center" }}>
                    {p.name} · {p.phone_e164}
                    <button onClick={() => removeOne(rep.template_name, p.username)}
                            disabled={busy}
                            data-testid={`recipient-remove-${rep.template_name}-${p.username}`}
                            style={{ border: "none", background: "transparent", color: "#B91C1C",
                                      cursor: "pointer", fontWeight: 700 }}>✕</button>
                  </span>
                ))}
              </div>

              {/* مُدرَجون بلا هاتف صالح (إن وُجدوا) — شفافية */}
              {(rep.entries || []).filter((u) => !(rep.resolved || []).some((r) => r.username === u)).length > 0 && (
                <div style={{ marginTop: 8, fontSize: 11, color: "#B45309" }}>
                  ⚠️ مُدرَجون لا يستقبلون (بلا هاتف صالح / دور غير مطابق):{" "}
                  {(rep.entries || []).filter((u) => !(rep.resolved || []).some((r) => r.username === u)).map((u) => (
                    <span key={u} style={{ display: "inline-flex", gap: 4, marginInlineEnd: 6 }}>
                      {u}
                      <button onClick={() => removeOne(rep.template_name, u)} disabled={busy}
                              data-testid={`recipient-remove-stale-${rep.template_name}-${u}`}
                              style={{ border: "none", background: "transparent", color: "#B91C1C", cursor: "pointer" }}>✕</button>
                    </span>
                  ))}
                </div>
              )}

              {/* إضافة من حسابات الفريق */}
              <div style={{ marginTop: 12, display: "flex", gap: 6 }}>
                <select data-testid={`recipient-select-${rep.template_name}`}
                        value={pick[rep.template_name] || ""}
                        onChange={(e) => setPick((p) => ({ ...p, [rep.template_name]: e.target.value }))}
                        style={{ flex: 1, borderRadius: 8, border: "1px solid #E7E5E4",
                                  padding: "6px 8px", fontSize: 12, background: "#fff" }}>
                  <option value="">— اختر حساب فريق —</option>
                  {options.map((c) => (
                    <option key={c.username} value={c.username}>
                      {c.name} ({c.role}){c.has_phone ? "" : " — بلا هاتف"}
                    </option>
                  ))}
                </select>
                <button onClick={() => addOne(rep.template_name)} disabled={busy}
                        data-testid={`recipient-add-${rep.template_name}`}
                        style={{ background: "#DCFCE7", color: "#166534", border: "1px solid transparent",
                                  borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                  ➕ أضف
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
